import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiGet, apiPost, apiPatch } from '../api'
import HealthStrip from './charts/HealthStrip'
import EstateOverview from './charts/EstateOverview'
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface AppRow {
  id: string
  name: string
  app_group: string
  url: string
  expected_status: number
  civo_resource_type: string | null
  civo_resource_id: string | null
  civo_cluster_id: string | null
  civo_size_label: string | null
  civo_status?: string | null
  civo_public_ip?: string | null
  last_status: string | null
  last_code: number | null
  last_latency_ms: number | null
  last_checked_at: number | null
  last_changed_at: number | null
  last_error: string | null
  auto_stop?: number
  auto_stop_last_run?: number | null
  auto_stop_last_error?: string | null
  deploy_repo?: string | null
  deploy_workflow?: string | null
  deploy_ref?: string | null
}

interface AppsResponse  { apps: AppRow[]; fetched_at: number }
interface CostResponse  {
  fetched_at: number
  subtotal_cents: number
  vat_cents: number
  total_cents: number
  line_count: number
  top_movers: { label: string; cost_cents: number; hours: number; status: string }[]
  has_token: boolean
}

const STATUS_COLOR: Record<string, string> = {
  up:        '#3ddc84',
  degraded:  '#ffb454',
  down:      '#ff6b81',
  unknown:   '#9aa6c2',
}
const VM_STATE_COLOR: Record<string, string> = {
  ACTIVE:    '#3ddc84',
  STOPPED:   '#9aa6c2',
  BUILDING:  '#ffb454',
  STOPPING:  '#ffb454',
  REBOOTING: '#ffb454',
  UNKNOWN:   '#9aa6c2',
}

function fmtCents(c: number): string {
  return `$${(c / 100).toFixed(2)}`
}
function fmtAge(ts: number | null | undefined): string {
  if (!ts) return '—'
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.floor(s/60)}m ago`
  if (s < 86400) return `${Math.floor(s/3600)}h ago`
  return `${Math.floor(s/86400)}d ago`
}

interface StatusCounts { up: number; degraded: number; down: number; unknown: number }
function countByStatus(rows: AppRow[]): StatusCounts {
  const c: StatusCounts = { up: 0, degraded: 0, down: 0, unknown: 0 }
  for (const r of rows) {
    const s = (r.last_status as keyof StatusCounts) || 'unknown'
    if (c[s] !== undefined) c[s] += 1
    else c.unknown += 1
  }
  return c
}

interface SortableGroupProps {
  group: string
  count: number
  counts: StatusCounts
  collapsed: boolean
  toggleCollapsed: () => void
  children: React.ReactNode
}
function SortableGroup({ group, count, counts, collapsed, toggleCollapsed, children }: SortableGroupProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: group })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 5 : 'auto',
  }
  return (
    <div ref={setNodeRef} style={style}
         className={`infra-group ${isDragging ? 'dragging' : ''} ${collapsed ? 'collapsed' : ''}`}>
      <div className="infra-group-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span ref={setActivatorNodeRef} {...attributes} {...listeners}
              style={{ cursor: 'grab', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}
              aria-label={`Drag ${group}`}>
          <span style={{ opacity: 0.5 }}>⋮⋮</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group}</span>
          {collapsed ? (
            <span style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
              {counts.up > 0      && <span className="count-pill ok">{counts.up}</span>}
              {counts.degraded > 0 && <span className="count-pill warm">{counts.degraded}</span>}
              {counts.down > 0    && <span className="count-pill crit">{counts.down}</span>}
              {counts.unknown > 0 && <span className="count-pill">{counts.unknown}</span>}
            </span>
          ) : (
            <span className="muted" style={{ marginLeft: 4 }}>· {count}</span>
          )}
        </span>
        <button type="button" onClick={toggleCollapsed} aria-label={collapsed ? 'Expand' : 'Collapse'}
          style={{ minHeight: 22, minWidth: 22, padding: '0 5px', fontSize: '0.7rem' }}>
          {collapsed ? '▾' : '▴'}
        </button>
      </div>
      {children}
    </div>
  )
}

export default function InfraPanel() {
  const [apps, setApps] = useState<AppsResponse | null>(null)
  const [cost, setCost] = useState<CostResponse | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      const [a, c] = await Promise.all([
        apiGet<AppsResponse>('/infra/apps'),
        apiGet<CostResponse>('/infra/cost'),
      ])
      setApps(a); setCost(c)
    } catch (e) {
      setErr(String(e))
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 30_000)
    return () => clearInterval(t)
  }, [load])

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null)
  function showToast(msg: string, ok = true) {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 3500)
  }

  async function action(id: string, kind: 'start' | 'stop') {
    if (busyId) return
    setBusyId(id)
    showToast(`${kind === 'start' ? 'Starting' : 'Stopping'} ${id}…`, true)
    try {
      const r = await apiPost<{ ok: boolean; error?: string }>(`/infra/apps/${id}/civo/${kind}`)
      if (!r.ok) {
        showToast(`${kind} failed: ${r.error || 'unknown'}`, false)
      } else {
        showToast(`${kind === 'start' ? 'Started' : 'Stopped'} ${id}`, true)
      }
      setTimeout(() => { setBusyId(null); load() }, 2000)
    } catch (e) {
      setBusyId(null)
      showToast(`${kind} failed: ${e}`, false)
    }
  }

  async function deploy(id: string, name: string) {
    if (!confirm(`Trigger GH Action deploy for ${name}?`)) return
    try {
      const r = await apiPost<{ ok?: boolean; repo?: string; ref?: string; error?: string }>(`/infra/apps/${id}/deploy`)
      if (r.ok) showToast(`🚀 dispatched ${r.repo}@${r.ref}`, true)
      else showToast(`deploy failed: ${r.error ?? 'unknown'}`, false)
    } catch (e) { showToast(`deploy failed: ${e}`, false) }
  }

  async function toggleAutoStop(id: string, on: boolean) {
    setApps(prev => prev ? { ...prev, apps: prev.apps.map(a => a.id === id ? { ...a, auto_stop: on ? 1 : 0 } : a) } : prev)
    try {
      await apiPatch(`/infra/apps/${id}`, { auto_stop: on })
      showToast(`auto-stop ${on ? 'enabled' : 'disabled'} for ${id}`, true)
    } catch (e) {
      showToast(`couldn't update: ${e}`, false)
      load()
    }
  }

  const [costBusy, setCostBusy] = useState(false)
  async function refreshCost() {
    if (costBusy) return
    setCostBusy(true)
    try {
      const r = await apiPost<{ ok: boolean; total_cents?: number; msg?: string }>('/infra/cost/refresh')
      if (!r.ok) setErr(r.msg || 'cost refresh failed')
      await load()
    } catch (e) { setErr(String(e)) }
    finally { setCostBusy(false) }
  }

  // Persisted custom group order + collapsed-state. Unknown groups append at
  // the end so newly discovered providers don't disappear.
  const ORDER_KEY = 'jamie-dashboard:infra-group-order'
  const COLLAPSED_KEY = 'jamie-dashboard:infra-group-collapsed'
  const [customOrder, setCustomOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY)
      return raw ? JSON.parse(raw) as string[] : []
    } catch { return [] }
  })
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY)
      return new Set<string>(raw ? JSON.parse(raw) : [])
    } catch { return new Set() }
  })

  function persistOrder(next: string[]) {
    setCustomOrder(next)
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }
  function toggleCollapsed(group: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group); else next.add(group)
      try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(next))) } catch { /* ignore */ }
      return next
    })
  }
  // dnd-kit: pointer sensor with a small activation distance so clicks on the
  // collapse / start / stop buttons inside the card don't accidentally start a drag.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  function onDndEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || !apps) return
    const src = String(active.id)
    const dest = String(over.id)
    if (src === dest) return
    const all = Array.from(new Set([...customOrder, ...apps.apps.map(a => a.app_group)]))
    const from = all.indexOf(src)
    const to   = all.indexOf(dest)
    if (from < 0 || to < 0) return
    persistOrder(arrayMove(all, from, to))
  }

  const groups = useMemo(() => {
    if (!apps) return []
    const m = new Map<string, AppRow[]>()
    for (const a of apps.apps) {
      if (!m.has(a.app_group)) m.set(a.app_group, [])
      m.get(a.app_group)!.push(a)
    }
    // Reorder by saved sequence; unknown groups keep their natural order at the tail.
    const ordered: Array<[string, AppRow[]]> = []
    const seen = new Set<string>()
    for (const k of customOrder) {
      if (m.has(k)) { ordered.push([k, m.get(k)!]); seen.add(k) }
    }
    for (const [k, v] of m) if (!seen.has(k)) ordered.push([k, v])
    return ordered as Array<[string, AppRow[]]>
  }, [apps, customOrder])


  return (
    <section className="panel infra">
      {toast && (
        <div className={`infra-toast ${toast.ok ? 'ok' : 'err'}`}>{toast.msg}</div>
      )}
      <div className="panel-header">
        <span>Infrastructure</span>
        {cost && (
          <button type="button" onClick={refreshCost} disabled={costBusy}
            style={{ minHeight: 26, padding: '2px 10px', fontSize: '0.66rem' }}>
            {costBusy ? '…' : 'refresh'}
          </button>
        )}
      </div>

      {err && <div className="empty-state">Couldn't load infra: {err}</div>}
      {cost && !cost.has_token && (
        <div className="empty-state" style={{ fontSize: '0.75rem', marginBottom: 8 }}>
          Set <code>CIVO_API_TOKEN</code> as a Worker secret to populate cost + enable start/stop.
        </div>
      )}

      {!apps && !err && <div className="empty-state">Loading…</div>}

      {apps && (
        <div className="chart-wrap">
          <EstateOverview apps={apps.apps} />
        </div>
      )}

      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={onDndEnd}
      >
        {(() => {
          const collapsed = groups.filter(([k]) => collapsedGroups.has(k))
          const expanded  = groups.filter(([k]) => !collapsedGroups.has(k))
          const allIds = [...collapsed.map(g => g[0]), ...expanded.map(g => g[0])]

          const renderRows = (items: AppRow[]) => (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {items.map(a => {
                const tint = STATUS_COLOR[a.last_status || 'unknown'] || '#9aa6c2'
                const vmTint = a.civo_status ? (VM_STATE_COLOR[a.civo_status] || '#9aa6c2') : null
                const isControllable = !!a.civo_resource_id && [
                  'instance','hetzner_server','hostinger_vps','kube_pool','kube_cluster'
                ].includes(a.civo_resource_type ?? '')
                const isBusy = busyId === a.id
                const autoStopOn = a.auto_stop === 1
                const showHealthStrip = !['railway_service','vercel_project'].includes(a.civo_resource_type ?? '')
                const sizeLine = a.civo_size_label || ''
                return (
                  <li key={a.id} className="infra-row" title={a.last_error || a.civo_size_label || ''}>
                    <span className="status-dot"
                      style={{ background: tint, boxShadow: `0 0 6px ${tint}66` }}
                      title={`${a.last_status || 'unknown'} · ${a.last_code ?? '—'}`} />
                    <span className="infra-row-main">
                      <div className="infra-row-name">{a.name}</div>
                      <div className="infra-row-meta">
                        <span>{a.last_status || 'unknown'}</span>
                        <span>· {fmtAge(a.last_checked_at)}</span>
                        {vmTint && <span style={{ color: vmTint }}>· {a.civo_status}</span>}
                        {sizeLine && <span className="infra-row-size">· {sizeLine}</span>}
                      </div>
                      {showHealthStrip && <HealthStrip appId={a.id} hours={24} />}
                    </span>
                    {(isControllable || a.deploy_workflow) && (
                      <span className="infra-row-actions">
                        {a.deploy_workflow && (
                          <button type="button"
                            onClick={() => deploy(a.id, a.name)}
                            title={`Deploy via GH Action: ${a.deploy_repo}/${a.deploy_workflow}@${a.deploy_ref ?? 'main'}`}
                            className="btn-deploy"
                            aria-label="Deploy">
                            🚀
                          </button>
                        )}
                        {isControllable && (
                          <>
                            <button type="button"
                              onClick={() => toggleAutoStop(a.id, !autoStopOn)}
                              title={`Auto-stop nightly at 23:00 BST · ${autoStopOn ? 'ON' : 'OFF'}${a.auto_stop_last_error ? ' · last error: ' + a.auto_stop_last_error : ''}`}
                              className={`btn-autostop ${autoStopOn ? 'on' : ''}`}
                              aria-label={`Auto-stop ${autoStopOn ? 'on' : 'off'}`}>
                              🌙
                            </button>
                            <button type="button" onClick={() => action(a.id, 'start')}
                              disabled={isBusy} className="btn-start" aria-label="Start">▶</button>
                            <button type="button" onClick={() => action(a.id, 'stop')}
                              disabled={isBusy} className="btn-stop" aria-label="Stop">■</button>
                          </>
                        )}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          )

          const groupCard = ([group, items]: [string, AppRow[]]) => (
            <SortableGroup key={group} group={group}
                           count={items.length}
                           counts={countByStatus(items)}
                           collapsed={collapsedGroups.has(group)}
                           toggleCollapsed={() => toggleCollapsed(group)}>
              {!collapsedGroups.has(group) && renderRows(items)}
            </SortableGroup>
          )

          return (
            <SortableContext items={allIds} strategy={rectSortingStrategy}>
              {collapsed.length > 0 && (
                <div className="infra-grid infra-grid-collapsed">
                  {collapsed.map(groupCard)}
                </div>
              )}
              {expanded.length > 0 && (
                <div className="infra-grid">
                  {expanded.map(groupCard)}
                </div>
              )}
            </SortableContext>
          )
        })()}
      </DndContext>

      {cost && cost.top_movers.length > 0 && (
        <details style={{ marginTop: 8, fontSize: '0.75rem' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--fg-dim, #9aa6c2)' }}>
            top {Math.min(5, cost.top_movers.length)} cost movers
          </summary>
          <ul style={{ margin: '4px 0 0', padding: '0 0 0 12px' }}>
            {cost.top_movers.slice(0, 5).map((m, i) => (
              <li key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                  {m.label}
                </span>
                <span style={{ flexShrink: 0, color: 'var(--fg-dim, #9aa6c2)' }}>
                  {fmtCents(m.cost_cents)} · {m.hours.toFixed(1)}h · {m.status}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
